import { useState } from "react";
import { ChevronDown, Building2, MapPin, Star, ShieldCheck, Phone, Mail, FileText, ImagePlus, X, AlertTriangle, Loader2 } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { uploadFile } from "../lib/uploads";
import VoiceTextarea from "./VoiceTextarea";
import DatePickerInput from "./DatePickerInput";

/**
 * SuspectEditor — the SAME editor used for create + edit, with every
 * column from the original PROSPECT LOG.xlsx as an optional field.
 *
 * Sections (collapsible, all expanded by default):
 *   • Identity & contact     (name, contact_name, position, phone, email)
 *   • Location               (industry, province_area, estate_reserve)
 *   • Property metrics       (lodge stars, price/night, has_wtp, has_sla, needs_system)
 *   • Sales pulse            (potential_client, lead_rating, current_provider, source, follow-up date)
 *   • Notes & gallery        (notes free-text, photo data-URL upload)
 *
 * On save, posts/puts to /api/suspects. Caller passes onSaved(suspect)
 * to refresh and close. If `value.id` is set, this is an edit; otherwise create.
 */

const INDUSTRIES = [
  "lodge", "hotel", "restaurant", "agriculture", "food_beverage",
  "industrial", "manufacturing", "mining", "retail", "office",
  "residential", "individual", "other",
];

const SOURCES = [
  "drive-by", "board photo", "referral", "cold call", "trade show",
  "website", "social", "existing customer", "imported_from_prospect_log", "other",
];

const SECTIONS = [
  { key: "identity", title: "Identity & contact", icon: Building2, accent: "#1FB6C1" },
  { key: "location", title: "Location", icon: MapPin, accent: "#A78BFA" },
  { key: "property", title: "Property metrics", icon: Star, accent: "#F59E0B" },
  { key: "sales", title: "Sales pulse", icon: ShieldCheck, accent: "#22C55E" },
  { key: "notes", title: "Notes & photo", icon: FileText, accent: "#06B6D4" },
];

function SectionFold({ section, open, onToggle, children }) {
  const Icon = section.icon;
  return (
    <div
      className="hv-card overflow-hidden"
      style={{ borderColor: open ? `${section.accent}40` : "rgba(255,255,255,0.05)" }}
      data-testid={`suspect-section-${section.key}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-white/[0.03] text-left"
        style={{ background: open ? `${section.accent}0a` : "transparent" }}
        data-testid={`suspect-section-toggle-${section.key}`}
      >
        <span className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: section.accent }} />
          <span className="text-sm font-bold" style={{ color: section.accent }}>{section.title}</span>
        </span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} style={{ color: section.accent }} />
      </button>
      {open && <div className="px-3 pb-3 pt-2 space-y-2.5">{children}</div>}
    </div>
  );
}

function Field({ label, children, half }) {
  return (
    <label className={`block ${half ? "" : "col-span-2"}`}>
      <span className="text-[10px] uppercase tracking-wider text-[#647087]">{label}</span>
      {children}
    </label>
  );
}

export default function SuspectEditor({ value, onSaved, onCancel, industriesOverride }) {
  const initial = {
    name: "", phone: "", email: "", industry: "", province_area: "", estate_reserve: "",
    notes: "", source: "", photo_data_url: "", photo_data_urls: [],
    photo_upload_id: "", photo_upload_ids: [],
    contact_name: "", contact_position: "",
    star_rating: "", price_per_night: "", price_bracket: "",
    has_wtp: null, has_sla: null, needs_system: null, potential_client: null,
    lead_rating: "", current_provider: "",
    first_contact_date: "", last_contact_date: "", next_followup_date: "",
    ...(value || {}),
  };
  const [form, setForm] = useState(initial);
  const [open, setOpen] = useState({ identity: true, location: true, property: true, sales: true, notes: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const fold = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  const onPhoto = async (e) => {
    // [Iter 322 — 2026-02-20] Route new suspect photos through R2 via
    // `/api/uploads` and hand the server an `upload_id` list instead of
    // stuffing base64 into Mongo. Legacy `photo_data_url(s)` fields
    // stay in the payload only when this widget is re-editing an old
    // suspect that already had them — new uploads exclusively feed
    // `photo_upload_ids`.
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = "";
    const uploadedIds = [];
    for (const f of files) {
      try {
        const up = await uploadFile(f, { source: "suspect_photo" });
        if (up?.id) uploadedIds.push(up.id);
      } catch {
        /* skip failures — the caller will see fewer thumbnails */
      }
    }
    if (uploadedIds.length === 0) return;
    const existing = Array.isArray(form.photo_upload_ids)
      ? form.photo_upload_ids
      : (form.photo_upload_id ? [form.photo_upload_id] : []);
    const next = [...existing, ...uploadedIds];
    set({
      photo_upload_ids: next,
      photo_upload_id: next[0] || "",  // legacy mirror for cover-photo consumers
    });
  };

  const removePhotoAt = (idx) => {
    // Deletion order matches how we render: legacy data-URLs first,
    // then R2 upload_ids appended. Keep them separated so we don't
    // splice across arrays and lose alignment.
    const legacy = Array.isArray(form.photo_data_urls)
      ? form.photo_data_urls
      : (form.photo_data_url ? [form.photo_data_url] : []);
    const uploads = Array.isArray(form.photo_upload_ids)
      ? form.photo_upload_ids
      : (form.photo_upload_id ? [form.photo_upload_id] : []);
    if (idx < legacy.length) {
      const nextLegacy = legacy.filter((_, i) => i !== idx);
      set({
        photo_data_urls: nextLegacy,
        photo_data_url: nextLegacy[0] || "",
      });
    } else {
      const upIdx = idx - legacy.length;
      const nextUploads = uploads.filter((_, i) => i !== upIdx);
      set({
        photo_upload_ids: nextUploads,
        photo_upload_id: nextUploads[0] || "",
      });
    }
  };

  const submit = async () => {
    if (!form.name?.trim()) {
      setErr("Name is required.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      // Strip empty strings for cleaner Mongo docs
      const clean = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null && v !== undefined),
      );
      // Coerce numeric fields
      if (clean.star_rating) clean.star_rating = Number(clean.star_rating);
      if (clean.lead_rating) clean.lead_rating = Number(clean.lead_rating);
      const isEdit = !!value?.id;
      const r = isEdit
        ? await api.put(`/suspects/${value.id}`, clean)
        : await api.post("/suspects", clean);
      onSaved?.(r.data);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not save"));
    } finally { setBusy(false); }
  };

  const industries = industriesOverride?.length ? industriesOverride : INDUSTRIES;
  const TriBool = ({ field, label }) => (
    <Field label={label} half>
      <select
        value={form[field] === true ? "yes" : form[field] === false ? "no" : ""}
        onChange={(e) => {
          const v = e.target.value;
          set({ [field]: v === "yes" ? true : v === "no" ? false : null });
        }}
        className="hv-input mt-1"
        data-testid={`suspect-${field}`}
      >
        <option value="">—</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </Field>
  );

  return (
    <div className="space-y-2.5" data-testid="suspect-editor">
      {err && (
        <div className="border border-rose-500/40 bg-rose-500/5 text-rose-300 text-xs p-2 rounded flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      <SectionFold section={SECTIONS[0]} open={open.identity} onToggle={() => fold("identity")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Name (lodge / business / contact) *">
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} className="hv-input mt-1" data-testid="suspect-name" autoFocus />
          </Field>
          <Field label="Contact person" half>
            <input value={form.contact_name} onChange={(e) => set({ contact_name: e.target.value })} className="hv-input mt-1" data-testid="suspect-contact-name" />
          </Field>
          <Field label="Position / role" half>
            <input value={form.contact_position} onChange={(e) => set({ contact_position: e.target.value })} className="hv-input mt-1" data-testid="suspect-contact-position" placeholder="e.g. GM, Maintenance Mgr" />
          </Field>
          <Field label="Phone" half>
            <div className="relative mt-1">
              <Phone className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="hv-input pl-8" data-testid="suspect-phone" />
            </div>
          </Field>
          <Field label="Email" half>
            <div className="relative mt-1">
              <Mail className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#647087]" />
              <input value={form.email} onChange={(e) => set({ email: e.target.value })} className="hv-input pl-8" data-testid="suspect-email" />
            </div>
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[1]} open={open.location} onToggle={() => fold("location")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Industry" half>
            <select value={form.industry} onChange={(e) => set({ industry: e.target.value })} className="hv-input mt-1" data-testid="suspect-industry">
              <option value="">—</option>
              {industries.map((i) => <option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
          <Field label="Province / area" half>
            <input value={form.province_area} onChange={(e) => set({ province_area: e.target.value })} className="hv-input mt-1" data-testid="suspect-province" />
          </Field>
          <Field label="Estate / reserve">
            <input value={form.estate_reserve} onChange={(e) => set({ estate_reserve: e.target.value })} className="hv-input mt-1" data-testid="suspect-estate" />
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[2]} open={open.property} onToggle={() => fold("property")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Star rating (1-5)" half>
            <input type="number" min="0" max="6" step="0.5" value={form.star_rating ?? ""} onChange={(e) => set({ star_rating: e.target.value })} className="hv-input mt-1" data-testid="suspect-star-rating" />
          </Field>
          <Field label="Price per night" half>
            <input value={form.price_per_night} onChange={(e) => set({ price_per_night: e.target.value })} className="hv-input mt-1" data-testid="suspect-price-night" placeholder="R20,000-up" />
          </Field>
          <TriBool field="has_wtp" label="Has WTP?" />
          <TriBool field="has_sla" label="Has SLA?" />
          <TriBool field="needs_system" label="Needs system?" />
          <TriBool field="potential_client" label="Potential client?" />
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[3]} open={open.sales} onToggle={() => fold("sales")}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
          <Field label="Source" half>
            <select value={form.source} onChange={(e) => set({ source: e.target.value })} className="hv-input mt-1" data-testid="suspect-source">
              <option value="">—</option>
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Lead rating (1-5)" half>
            <input type="number" min="1" max="5" value={form.lead_rating ?? ""} onChange={(e) => set({ lead_rating: e.target.value })} className="hv-input mt-1" data-testid="suspect-lead-rating" />
          </Field>
          <Field label="Current provider" half>
            <input value={form.current_provider} onChange={(e) => set({ current_provider: e.target.value })} className="hv-input mt-1" data-testid="suspect-current-provider" placeholder="e.g. WaterPro Co." />
          </Field>
          <Field label="Next follow-up" half>
            <DatePickerInput value={(form.next_followup_date || "").slice(0, 10)} onChange={(v) => set({ next_followup_date: v })} testId="suspect-next-followup" />
          </Field>
          <Field label="First contact" half>
            <DatePickerInput value={(form.first_contact_date || "").slice(0, 10)} onChange={(v) => set({ first_contact_date: v })} testId="suspect-first-contact" />
          </Field>
          <Field label="Last contact" half>
            <DatePickerInput value={(form.last_contact_date || "").slice(0, 10)} onChange={(v) => set({ last_contact_date: v })} testId="suspect-last-contact" />
          </Field>
        </div>
      </SectionFold>

      <SectionFold section={SECTIONS[4]} open={open.notes} onToggle={() => fold("notes")}>
        <Field label="Notes">
          <VoiceTextarea
            value={form.notes}
            onChange={(v) => set({ notes: v })}
            rows={4}
            className="mt-1"
            context="lead_activity"
            testIdPrefix="suspect-notes-voice"
            data-testid="suspect-notes"
          />
        </Field>
        <Field label="Photos (optional, multi-select OK)">
          <div className="mt-1 flex items-start gap-3 flex-wrap">
            <label className="hv-btn hv-btn-ghost text-xs cursor-pointer">
              <ImagePlus className="w-3.5 h-3.5" /> Add photos
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={onPhoto}
                className="hidden"
                data-testid="suspect-photo-input"
              />
            </label>
            {(() => {
              // [Iter 322] Render legacy base64 previews first, then
              // R2 upload_id previews via /api/uploads/<id>.
              const legacy = Array.isArray(form.photo_data_urls)
                ? form.photo_data_urls
                : (form.photo_data_url ? [form.photo_data_url] : []);
              const uploads = Array.isArray(form.photo_upload_ids)
                ? form.photo_upload_ids
                : (form.photo_upload_id ? [form.photo_upload_id] : []);
              const all = [
                ...legacy.map((u) => ({ src: u, key: `l-${u.slice(0, 24)}` })),
                ...uploads.map((id) => ({ src: `/api/uploads/${id}`, key: `u-${id}` })),
              ];
              return all.map((p, idx) => (
                <div key={p.key} className="relative">
                  <img
                    src={p.src}
                    alt={`Suspect ${idx + 1}`}
                    className="w-20 h-20 rounded object-cover border border-white/10"
                    data-testid={`suspect-photo-preview-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removePhotoAt(idx)}
                    className="absolute -top-2 -right-2 bg-black/80 rounded-full p-0.5 text-rose-300 border border-white/20"
                    data-testid={`suspect-photo-clear-${idx}`}
                  ><X className="w-3 h-3" /></button>
                </div>
              ));
            })()}
          </div>
        </Field>
      </SectionFold>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="hv-btn hv-btn-ghost" data-testid="suspect-cancel">Cancel</button>
        <button type="button" onClick={submit} disabled={busy} className="hv-btn hv-btn-primary" data-testid="suspect-submit">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {value?.id ? "Save changes" : "Capture"}
        </button>
      </div>
    </div>
  );
}
